import { useEffect, useState } from 'react';
import { CIInfoIcon, DatePicker, Dropdown } from 'ccs-ui-components';
import { useTranslation } from 'react-i18next';

import { IJobSchedulerProps, typeFrequency } from '../../utilities/TypeDefs';
import { getTimeValues, ISelectProps, maxScheduleVal } from '../../utilities/Utility';
import { MonthlyComp } from '../schedule-view/MonthlyComp';
import { WeeklyComp } from '../schedule-view/WeekComp';
import classes from './JobScheduler.module.scss';

const hoursList = getTimeValues(24);

const minutesList = getTimeValues(60);

export const JobScheduler = ({
  startDate,
  startDateHandler,
  startHour,
  startMinute,
  startHourHandler,
  startMinuteHandler,
  blurHandler,
  keyUpHandler,
  keydownHandler,
  repetition,
  schedule,
  scheduleHandler,
  endDate,
  endDateHandler,
  endHour,
  endMinute,
  endHourHandler,
  endMinuteHandler,
  weekDays,
  setWeekDays,
  monthDay,
  monthDayHandler,
  listWeek,
  listWeekHandler,
  listDays,
  listDaysHandler,
  monthRadio,
  monthRadioHandler }: IJobSchedulerProps) => {
  const { t: translate } = useTranslation();

  const scheduleOptions = [
    { value: 'hourly', name: translate('job_scheduler.txt_hour') },
    { value: 'daily', name: translate('job_scheduler.txt_day') },
    { value: 'weekly', name: translate('job_scheduler.txt_week') },
    { value: 'monthly', name: translate('job_scheduler.txt_month') }
  ];
  const [startHoursListVal, setStartHoursListVal] = useState(getTimeValues(24));
  const [endHoursListVal, setEndHoursListVal] = useState(getTimeValues(24));

  const isToday = (date: Date | null) => {
    const today = new Date();
    if (date) {
      return date.getFullYear() === today.getFullYear() && date.getMonth() === today.getMonth() && date.getDate() === today.getDate();
    }
    return false;
};

  useEffect(() => {
  const d = new Date();
  const hour = d.getHours();
  const isTodayStartDay = isToday(startDate);
  const isTodayEndDay = isToday(endDate);
    if (isTodayStartDay && hour !== 23) {
      setStartHoursListVal(hoursList.slice(hour));
    } else {
      setStartHoursListVal(hoursList);
    }

    if (isTodayEndDay && hour !== 23) {
      setEndHoursListVal(hoursList.slice(hour));
    } else {
      setEndHoursListVal(hoursList);
    }
  }, [startDate, endDate]);

  return (
    <div className={classes['scheduler-container']}>
      <p className={classes['recurrence-txt']}>
        <span className={classes.star}>*</span>
        {translate('job_scheduler_component.txt_job_scheduler_recurrence')}
        <CIInfoIcon iconTxt={translate('job_scheduler_component.set_recurrence_info_bubble')} />
      </p>
      <div className={classes['scheduler-body']}>
        <div className={classes['start-date']}>
          <p className={classes['start-txt']}>{translate('job_scheduler_component.lbl_job_scheduler_start_date')} <CIInfoIcon iconTxt={translate('job_scheduler_component.start_date_info_bubble')} /></p>
          <DatePicker
            selected={startDate}
            onChange={startDateHandler}
            minDate={new Date()}
            maxDate={endDate ?? undefined}
            placeholderText={translate('job_scheduler.txt_start_date') ?? 'Start Date'}
          />

          <div className={classes['time-dropdown']}>
            <Dropdown
              label=""
              defaultValue={startHour}
              data={startHoursListVal}
              onChange={startHourHandler}
              className={classes.hour}
              data-testid="startHour"
            />
            <Dropdown
              label=""
              defaultValue={startMinute}
              data={minutesList}
              onChange={startMinuteHandler}
              className={classes.minute}
              data-testid="startMinute"
            />
          </div>
        </div>
        <div className={classes['repeats-every']}>
          <p className={classes['repeat-every-txt']}>{translate('job_scheduler_component.txt_job_scheduler_repeat')} <CIInfoIcon iconTxt={translate('job_scheduler_component.repeats_every_info_bubble')} /></p>
          <div className={classes['input-dropdown']}>
            <input
              type="number"
              className={classes['page-input']}
              onBlur={blurHandler}
              onKeyUp={keyUpHandler}
              onKeyDown={keydownHandler}
              defaultValue={repetition + 1}
              key={repetition + 1}
              min={1}
              max={maxScheduleVal(schedule)}
              data-testid="frequency"
              aria-label={translate('job_scheduler_component.txt_job_scheduler_repeat').toString()}
            />
            <Dropdown
              label=""
              data={scheduleOptions}
              className={classes['dropdown-control']}
              value={schedule}
              onChange={scheduleHandler}
              placeholder={String(translate('job_scheduler_component.lbl_frequency_dropdown'))}
              data-testid="scheduleType"
            />
          </div>
        </div>
        {schedule === typeFrequency.WEEKLY && <WeeklyComp selectedDays={weekDays} setSelectedDays={setWeekDays} />}
        {schedule === typeFrequency.MONTHLY && <MonthlyComp startDay={monthDay} monthDaysHandler={monthDayHandler} listWeek={listWeek} listWeekHandler={listWeekHandler} listDays={listDays} listDaysHandler={listDaysHandler} monthRadio={monthRadio} monthRadioHandler={monthRadioHandler} />}
        <div className={classes['end-date']}>
          <p className={classes['end-txt']}>{translate('job_scheduler_component.lbl_job_scheduler_end_date')} <CIInfoIcon iconTxt={translate('job_scheduler_component.end_date_info_bubble')} /></p>
          <DatePicker
            selected={endDate}
            onChange={endDateHandler}
            minDate={startDate ?? undefined}
            placeholderText={translate('job_scheduler.txt_select_date') ?? 'Select Date'}
          />
          <div className={classes['time-dropdown']}>
            <Dropdown
              label=""
              defaultValue={endHour}
              data={endHoursListVal}
              onChange={endHourHandler}
              className={classes.hour}
              data-testid="endHour"
            />
            <Dropdown
              label=""
              defaultValue={endMinute}
              data={minutesList}
              onChange={endMinuteHandler}
              className={classes.minute}
              data-testid="endMinute"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
